import {observable, action, computed} from 'mobx'
import {mergeDeep} from 'Utils'
import {notification} from 'antd'

export class Charts {
  @observable indicators
  @observable pnl
  @observable price
  @observable transactions
  @observable transactionsIndicators
  @observable triggeredDays
  @observable frequency
  @observable labelFormat

  constructor(rootStore, type) {
    this.root = rootStore
    this.api = rootStore.api
    this.type = type

    this.price = {
      loading: false,
      failed: false,
      keys: {},
      ticker: ''
    }
    this.pnl = {
      loading: false,
      failed: false,
      keys: {}
    }
    this.indicators = {
      loading: false,
      failed: false,
      keys: {},
      consts: {},
      units: {},
      list: []
    }
    this.transactions = {
      loading: false,
      failed: false,
      keys: {}
    }
    this.transactionsIndicators = {}
    this.triggeredDays = {}
    this.labelFormat = '%Y-%m-%d'
  }

  @computed get singleTicker() {
    const keys = mergeDeep({}, this.pnl.keys, this.price.keys, this.indicators.keys, this.triggeredDays)

    const dates = Object.keys(keys) || []
    let data = []
    for (let i=0; i<dates.length; i++) {
      let obj = {...keys[dates[i]]}
      data.push({
        ts: dates[i],
        ...obj
      })
    }
    data.sort((a,b) => new Date(a.ts) - new Date(b.ts))
    while (data.length < 30) {
      let d
      if (data[0]) {
        d = new Date(data[data.length - 1].ts)
        switch (this.frequency) {
        case 'yearly':
          d.setFullYear(d.getFullYear() + 1)
          break
        case 'monthly':
          d.setMonth(d.getMonth() + 1)
          break
        case 'weekly':
          d.setDate(d.getDate() + 7)
          break
        case 'daily':
          d.setDate(d.getDate() + 1)
          break
        case 'hourly':
          d.setTime(d.getTime() + (60*60*1000))
          break
        case 'minutely':
          d.setTime(d.getTime() + (60*1000))
          break
        case 'secondly':
          d.setTime(d.setTime() + 1000)
          break
        default:
          d.setDate(d.getDate() + 1)
          break
        }
      } else {
        d = new Date()
      }
      const emptyRow = {
        ts: d.toISOString()
      }
      data.push(emptyRow)
    }
    return data
  }

  @computed get multipleTickers() {
    const keys = mergeDeep({}, this.pnl.keys, this.transactions.keys)

    const dates = Object.keys(keys) || []
    let data = []
    for (let i=0; i<dates.length; i++) {
      const obj = {...keys[dates[i]]}
      data.push({
        ts: dates[i],
        ...obj
      })
    }
    data.sort((a,b) => new Date(a.ts) - new Date(b.ts))
    while (data.length < 2) {
      let d
      if (data.length) {
        d = new Date(data[data.length - 1].ts)
        switch (this.frequency) {
        case 'yearly':
          d.setFullYear(d.getFullYear() + 1)
          break
        case 'monthly':
          d.setMonth(d.getMonth() + 1)
          break
        case 'weekly':
          d.setDate(d.getDate() + 7)
          break
        case 'daily':
          d.setDate(d.getDate() + 1)
          break
        case 'hourly':
          d.setTime(d.getTime() + (60*60*1000))
          break
        case 'minutely':
          d.setTime(d.getTime() + (60*1000))
          break
        case 'secondly':
          d.setTime(d.setTime() + 1000)
          break
        default:
          d.setDate(d.getDate() + 1)
          break
        }
      } else {
        d = new Date()
      }
      const emptyRow = {
        ts: d.toISOString(),
        buy: 0,
        sell: 0
      }
      data.push(emptyRow)
    }
    return data
  }

  @action changeFrequency(frequency) {
    this.frequency = frequency
    switch (frequency) {
    case 'yearly':
      this.labelFormat = '%Y'
      break
    case 'monthly':
      this.labelFormat = '%Y-%m'
      break
    case 'weekly':
      this.labelFormat = '%Y-%m-%d'
      break
    case 'daily':
      this.labelFormat = '%Y-%m-%d'
      break
    case 'hourly':
      this.labelFormat = '%Y-%m-%d %H:%M'
      break
    case 'minutely':
      this.labelFormat = '%Y-%m-%d %H:%M'
      break
    case 'secondly':
      this.labelFormat = '%Y-%m-%d %H:%M:%S'
      break
    default:
      this.labelFormat = '%Y-%m-%d'
      break
    }
  }

  @action fetchCombined(id, ticker) {
    this.price.keys = {}
    this.indicators.keys = {}
    this.triggeredDays = {}
    this.price.failed = false
    this.price.loading = true
    let path = `sys/${this.type}/${id}/charts`
    if (ticker) {
      path =`${path}?ticker=${ticker}`
    }
    return this.root.api.request(path, 'get'
    ).then(resObj => {
      this.price.ticker = resObj.ticker
      this.processPrice(resObj.candlestick_data)
      this.processIndicators(resObj.indicators_data)
      this.processTriggeredDaysData(resObj.triggered_days_data)
    }).catch((e) => {
      notification['error']({
        message: 'Failed to load price chart',
        description: e.message || 'Unknown error'
      })
      this.price.failed = true
      this.price.loading = false
    })
  }

  @action processPrice(main) {
    if (!main) {
      this.price.loading = false
      this.price.failed = true
      return
    }
    let keys = {}
    main.ts.map((ts, index) => {
      const dt = ts
      keys[dt] = {
        transactions_amount: main.transactions_amount[index],
        transactions_price: main.transactions_price[index],
        high: main.high[index],
        low: main.low[index],
        open: main.open[index],
        close: main.close[index]
      }
    })
    this.price.keys = keys
    this.price.loading = false
  }

  @action processIndicators(data) {
    if (!data) {
      this.indicators.loading = false
      this.indicators.failed = true
      return
    }
    let consts = {}
    data.const_values.map(row => {
      consts[row.chart_id] = row.value
    })
    this.indicators.consts = consts

    let units = {}
    let list = []

    let keys = {}
    data.indicators.map((el) => {
      let item = list.find(row => row.chart_id === `${el.chart_id}`)
      if (!item) {
        item = {
          chart_id: `${el.chart_id}`,
          names: []
        }
        list.push(item)
      }
      item.names.push(el.name)
      units[el.chart_id] = {unit:el.unit ? el.unit.symbol : '', location:el.unit ? el.unit.location : ''}

      const max = Math.min(el.data.ts.length, el.data.values.length)

      for (let i=0; i<max; i++) {
        const dt = el.data.ts[i]

        if (keys[dt] === undefined) {
          keys[dt] = {indicators:{}}
        }
        if (keys[dt].indicators[el.chart_id] === undefined) {
          keys[dt].indicators[el.chart_id] = {}
        }
        keys[dt].indicators[el.chart_id][el.name] = el.data.values[i]
      }
    })

    data.complex_values.map(val => {
      let item = list.find(row => row.chart_id === val.chart_id)
      if (!item) {
        item = {
          chart_id: val.chart_id,
          names: []
        }
        list.push(item)
      }
      item.names.push(val.name)
      units[val.chart_id] = {unit:val.unit ? val.unit.symbol : '', location:val.unit ? val.unit.location : ''}

      const max = Math.min(val.data.ts.length, val.data.values.length)

      for (let i=0; i<max; i++) {
        const dt = val.data.ts[i]
        if (keys[dt] === undefined) {
          keys[dt] = {indicators:{}}
        }
        if (keys[dt].indicators[val.chart_id] === undefined) {
          keys[dt].indicators[val.chart_id] = {}
        }
        keys[dt].indicators[val.chart_id][val.name] = val.data.values[i]
      }
    })

    this.indicators.list = list
    this.indicators.units = units
    this.indicators.keys = keys
    this.indicators.loading = false
  }

  @action processTriggeredDaysData(data) {
    let keys = {}
    data.map(row => {
      let indicators = {}
      row.indicators.map(ind => {
        indicators[ind.name] = {
          chart_id: ind.chart_id,
          unit: ind.unit,
          value: ind.value
        }
      })
      const dt = row.ts
      keys[dt] = {
        triggeredDays: indicators,
        type: row.type,
        transaction: row.transaction,
        triggeredOnAggr: row.is_triggered_on_aggr_backtest,
        message: row.message
      }
    })
    this.triggeredDays = keys
  }

  @action processTransactionsIndicatorsData(data) {
    let keys = {}
    data.map(row => {
      let indicators = {}
      row.indicators.map(ind => {
        indicators[ind.name] = {
          chart_id: ind.chart_id,
          unit: ind.unit,
          value: ind.value
        }
      })
      const dt = row.ts
      keys[dt] = {transactionsIndicators: indicators}
    })
    this.transactionsIndicators = keys
  }

  @action fetchPnl(id, ticker) {
    this.pnl.keys = {}
    this.pnl.failed = false
    this.pnl.loading = true
    let path = `sys/${this.type}/${id}/charts/pnl`
    if (ticker) {
      path =`${path}?ticker=${ticker}`
    }
    return this.root.api.request(path, 'get'
    ).then(resObj => {
      let keys = {}
      resObj.data.map(row => {
        const dt = row.ts
        keys[dt] = {pnl: row.value}
      })
      this.pnl.keys = keys
      this.pnl.loading = false
    }).catch((e) => {
      notification['error']({
        message: 'Failed to load PnL chart',
        description: e.message || 'Unknown error'
      })
      this.pnl.failed = true
      this.pnl.loading = false
    })
  }

  @action fetchIndicators(id, ticker) {
    this.indicators.keys = {}
    this.indicators.failed = false
    this.indicators.loading = true
    let path = `sys/${this.type}/${id}/charts/indicators`
    if (ticker) {
      path =`${path}?ticker=${ticker}`
    }
    return this.root.api.request(path, 'get'
    ).then(resObj => {

      /* removing duplicates */
      resObj.data.indicators.map(indicator => {
        indicator.data.ts.map((ts, index) => {
          if (ts === indicator.data.ts[index-1]) {
            indicator.data.ts.splice(index-1, 1)
            indicator.data.values.splice(index-1, 1)
          }
        })
      })
      /* / */

      let consts = {}
      resObj.data.const_values.map(row => {
        consts[row.chart_id] = row.value
      })
      this.indicators.consts = consts

      let units = {}
      resObj.data.indicators.map(row => {
        units[row.chart_id] = {unit:row.unit ? row.unit.symbol : '', location:row.unit ? row.unit.location : ''}
      })
      this.indicators.units = units

      let keys = {}
      resObj.data.indicators.map(ind => {
        const max = Math.min(ind.data.ts.length, ind.data.values.length)

        for (let i=0; i<max; i++) {
          const dt = ind.data.ts[i]
          if (keys[dt] === undefined) {
            keys[dt] = {indicators:{}}
          }
          if (keys[dt].indicators[ind.chart_id] === undefined) {
            keys[dt].indicators[ind.chart_id] = {}
          }
          keys[dt].indicators[ind.chart_id][ind.name] = ind.data.values[i]
        }
      })

      resObj.data.complex_values.map(val => {
        const max = Math.min(val.data.ts.length, val.data.values.length)

        for (let i=0; i<max; i++) {
          const dt = val.data.ts[i]
          if (keys[dt] === undefined) {
            keys[dt] = {indicators:{}}
          }
          if (keys[dt].indicators[val.chart_id] === undefined) {
            keys[dt].indicators[val.chart_id] = {}
          }
          keys[dt].indicators[val.chart_id][val.name] = val.data.values[i]
        }
      })

      return keys

    }).then(keys => {
      this.indicators.keys = keys
      this.indicators.loading = false
    }).catch((e) => {
      notification['error']({
        message: 'Failed to load indicators chart',
        description: e.message || 'Unknown error'
      })
      this.indicators.failed = true
      this.indicators.loading = false
    })
  }

  @action fetchTransactions(id, ticker) {
    this.transactions.keys = {}
    this.transactions.failed = false
    this.transactions.loading = true
    let path = `sys/${this.type}/${id}/charts/transactions`
    if (ticker) {
      path =`${path}?ticker=${ticker}`
    }
    return this.root.api.request(path, 'get'
    ).then(resObj => {
      let keys = {}
      resObj.data.buying_transactions.map(row => {
        const {ts, value} = row
        const dt = ts

        if (keys[dt] === undefined) {
          keys[dt] = {}
        }
        keys[dt].buy = value
      })
      resObj.data.selling_transactions.map(row => {
        const {ts, value} = row
        const dt = ts

        if (keys[dt] === undefined) {
          keys[dt] = {}
        }
        keys[dt].sell = value
      })
      this.transactions.keys = keys
      this.transactions.loading = false
    }).catch((e) => {
      notification['error']({
        message: 'Failed to load transactions chart',
        description: e.message || 'Unknown error'
      })
      this.transactions.failed = true
      this.transactions.loading = false
    })
  }
}
